import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, platformLabel } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PageHero from "../components/PageHero";
import Loading from "../components/Loading";
import { SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const FILTERS = {
  category: [
    { v: "", l: "All" },
    { v: "consoles", l: "Consoles" },
    { v: "games", l: "Games" },
    { v: "accessories", l: "Accessories" },
    { v: "controllers", l: "Controllers" },
    { v: "digital", l: "Digital" },
  ],
  platform: [
    { v: "", l: "All" },
    { v: "ps5", l: "PS5" },
    { v: "ps4", l: "PS4" },
    { v: "switch 2", l: "Switch 2" },
    { v: "switch 1", l: "Switch 1" },
    { v: "xbox", l: "Xbox" },
  ],
  condition: [
    { v: "", l: "All" },
    { v: "new", l: "New" },
    { v: "used", l: "Used" },
    { v: "upcoming", l: "Pre-Order" },
  ],
};

const PAGE_SIZE = 40; // 5 cols × 8 rows

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  const sort = params.get("sort") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/products", {
          params: {
            category: params.get("category") || undefined,
            platform: params.get("platform") || undefined,
            condition: params.get("condition") || undefined,
            sort: sort || undefined,
            page,
            page_size: PAGE_SIZE,
          },
        });
        const data = r.data;
        const items = data.items ?? (Array.isArray(data) ? data : []);
        const total = data.total ?? items.length;
        setProducts(items);
        setTotal(total);
        setTotalPages(data.total_pages ?? Math.ceil(total / PAGE_SIZE));
      } catch {
        setProducts([]);
        setTotal(0);
        setTotalPages(1);
      }

      setLoading(false);
    })();
  }, [params, sort, page]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    if (k !== "page") np.delete("page"); // reset to page 1 on filter change
    setParams(np);
  };

  const setPage = (p) => {
    const np = new URLSearchParams(params);
    if (p === 1) np.delete("page"); else np.set("page", String(p));
    setParams(np);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearAll = () => setParams(new URLSearchParams());

  const activeCount = ["category", "platform", "condition"].filter((k) => params.get(k)).length;

  const category = params.get("category");
  const condition = params.get("condition");
  const platform = params.get("platform");

  const pageTitle = category
    ? `${category.charAt(0).toUpperCase() + category.slice(1)} — GamesTrade`
    : condition === "used"
    ? "Used Deals — GamesTrade"
    : condition === "upcoming"
    ? "Pre-Book — GamesTrade"
    : platform
    ? `${platformLabel(platform)} — GamesTrade`
    : "Shop Everything Gaming — GamesTrade";

  const pageDesc = category
    ? `Buy ${category} at GamesTrade, Noida. Sony Authorised dealer. New, used and pre-book available.`
    : "Buy new, sell used, pre-book upcoming — PS5, Nintendo Switch, Xbox, games and accessories. Sony Authorised. Noida.";

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href="https://gamestrade.in/shop" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content="https://gamestrade.in/shop" />
        <meta property="og:type" content="website" />
      </Helmet>
      <PageHero
        eyebrow="Shop"
        title={<>Everything <span className="text-gold">gaming</span></>}
        description="Browse our complete catalogue — new releases, certified pre-owned, upcoming pre-books and accessories."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Shop" }]}
        size="compact"
      />

      <div className="site-container py-10">

      {/* Top bar: filter toggle + active pills + count */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => setPanelOpen((v) => !v)}
          className={`flex items-center gap-2 border rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition ${
            panelOpen
              ? "bg-gold text-navy-900 border-gold"
              : "bg-navy-700 border-gold/20 hover:border-gold/50"
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeCount > 0 && (
            <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${panelOpen ? "bg-navy-900 text-gold" : "bg-gold text-navy-900"}`}>
              {activeCount}
            </span>
          )}
          <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${panelOpen ? "rotate-180" : ""}`} />
        </button>

        {/* Active filter pills */}
        {["category", "platform", "condition"].map((k) => {
          const v = params.get(k);
          if (!v) return null;
          const display = k === "platform" ? platformLabel(v) : v.charAt(0).toUpperCase() + v.slice(1);
          return (
            <button
              key={k}
              onClick={() => setFilter(k, "")}
              className="flex items-center gap-1.5 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5 hover:bg-gold/20 transition"
            >
              {display} <X className="w-3 h-3" />
            </button>
          );
        })}

        <div className="ml-auto text-xs text-slate-400 uppercase tracking-wider">{total} products</div>
      </div>

      {/* Inline animated filter panel */}
      <div
        ref={panelRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: panelOpen ? "500px" : "0px", opacity: panelOpen ? 1 : 0 }}
      >
        <div className="bg-navy-700/60 border border-gold/15 rounded-2xl p-5 mb-6 backdrop-blur">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {Object.entries(FILTERS).map(([key, opts]) => (
              <div key={key} data-testid={`filter-${key}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold/70 mb-2">{key}</div>
                <div className="flex flex-col gap-0.5">
                  {opts.map((o) => {
                    const active = (params.get(key) || "") === o.v;
                    return (
                      <button
                        key={o.v}
                        onClick={() => setFilter(key, o.v)}
                        data-testid={`filter-${key}-${o.v || "all"}`}
                        className={`text-left text-sm px-3 py-1.5 rounded-md transition ${
                          active ? "bg-gold text-navy-900 font-bold" : "text-slate-300 hover:bg-navy-600"
                        }`}
                      >
                        {o.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Sort */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold/70 mb-2">Sort</div>
              <select
                value={sort}
                onChange={(e) => setFilter("sort", e.target.value)}
                className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold"
              >
                <option value="">Relevant</option>
                <option value="price_low">Price: Low → High</option>
                <option value="price_high">Price: High → Low</option>
              </select>
            </div>
          </div>

          {activeCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gold/10 flex items-center justify-between">
              <span className="text-xs text-slate-400">{activeCount} filter{activeCount > 1 ? "s" : ""} active</span>
              <button
                onClick={clearAll}
                className="text-xs uppercase tracking-wider text-rose-400 hover:text-rose-300 font-bold"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Products grid */}
      {loading ? (
        <div className="text-center py-20"><Loading variant="block" label="Loading" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No products match your filters.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-10">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1.5 border border-gold/30 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition disabled:opacity-30 hover:border-gold/60 hover:bg-navy-700"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-9 h-9 rounded-full text-sm font-bold transition ${
                  p === page
                    ? "bg-gold text-navy-900"
                    : "text-slate-400 hover:bg-navy-700 hover:text-white"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="flex items-center gap-1.5 border border-gold/30 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition disabled:opacity-30 hover:border-gold/60 hover:bg-navy-700"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {!loading && totalPages > 0 && (
        <div className="text-center mt-4 text-xs text-slate-500 uppercase tracking-wider">
          Page {page} of {totalPages} · {total} products
        </div>
      )}
      </div>
    </div>
  );
}
